<template>
  <div>
    <b-card class="border-0 shadow-sm mb-3">
      <b-row align-v="center">
        <b-col md="8">
          <h5 class="mb-0 font-weight-bold text-primary">
            <i class="fa fa-tint mr-2"></i>Histórico de Abastecimentos
          </h5>
          <small class="text-muted">Gerenciamento completo de abastecimentos e geração de títulos a pagar</small>
        </b-col>
        <b-col md="4" class="text-right">
          <b-button variant="success" @click="novoAbastecimento" class="mr-2" pill>
            <i class="fa fa-plus mr-1"></i> Novo
          </b-button>
          <b-button variant="outline-primary" @click="buscarAbastecimentos" pill>
            <i class="fa fa-sync mr-1"></i> Atualizar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card class="border-0 shadow-sm">
      <b-table
        :items="lista"
        :fields="fields"
        hover
        striped
        small
        show-empty
        responsive
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-2">Carregando abastecimentos...</strong>
          </div>
        </template>

        <template #empty>
          <div class="text-center text-muted my-5">
            <i class="fa fa-gas-pump fa-3x mb-3 opacity-50"></i>
            <h5>Nenhum abastecimento registrado</h5>
            <p class="mb-0">Clique em "Novo" para adicionar o primeiro abastecimento</p>
          </div>
        </template>

        <template #cell(DATA)="row">
          <span class="font-weight-bold">{{ formatarData(row.item.DATA) }}</span>
        </template>

        <template #cell(LITROS)="row">
          <span class="text-primary font-weight-bold">{{ formatarNumero(row.item.LITROS) }}</span>
        </template>

        <template #cell(PRECO)="row">
          <span class="text-info">{{ formatarMoeda(row.item.PRECO) }}</span>
        </template>

        <template #cell(TOTAL)="row">
          <span class="text-success font-weight-bold">{{ formatarMoeda(row.item.TOTAL) }}</span>
        </template>

        <template #cell(ULTIMA_KM)="row">
          <span class="text-muted">{{ formatarNumero(row.item.ULTIMA_KM) }} km</span>
        </template>

        <template #cell(OP_GERA_TITULO)="row">
          <b-badge :variant="row.item.OP_GERA_TITULO === '1' ? 'success' : 'secondary'" pill>
            {{ row.item.OP_GERA_TITULO === '1' ? 'Gerado' : 'Não Gerado' }}
          </b-badge>
        </template>

        <template #cell(actions)="row">
          <b-button-group size="sm">
            <b-button variant="outline-primary" @click="editarAbastecimento(row.item)" v-b-tooltip.hover title="Editar">
              <i class="fa fa-edit"></i>
            </b-button>
            <b-button 
              variant="outline-success" 
              @click="gerarTitulo(row.item)" 
              v-if="row.item.OP_GERA_TITULO !== '1'" 
              v-b-tooltip.hover 
              title="Gerar Título a Pagar"
            >
              <i class="fa fa-file-invoice-dollar"></i>
            </b-button>
            <b-button variant="outline-danger" @click="excluir(row.item)" v-b-tooltip.hover title="Excluir">
              <i class="fa fa-trash"></i>
            </b-button>
          </b-button-group>
        </template>
      </b-table>

      <!-- Totalizador -->
      <div v-if="lista.length > 0" class="border-top pt-3 mt-3">
        <b-row>
          <b-col md="3">
            <div class="p-3 bg-light rounded text-center">
              <small class="modal-label-premium">Total Abastecido</small>
              <h5 class="mb-0 font-weight-bold text-dark">{{ totalAbastecimentos }}</h5>
            </div>
          </b-col>
          <b-col md="3">
            <div class="p-3 bg-light rounded text-center">
              <small class="modal-label-premium">Abastecimentos c/ Título</small>
              <h5 class="mb-0 font-weight-bold text-success">{{ abastecimentosComTitulo }}</h5>
            </div>
          </b-col>
          <b-col md="3">
            <div class="p-3 bg-light rounded text-center">
              <small class="modal-label-premium">Títulos a Gerar</small>
              <h5 class="mb-0 font-weight-bold text-warning">{{ titulosEmAberto }}</h5>
            </div>
          </b-col>
          <b-col md="3">
            <div class="p-3 bg-success rounded text-center border-2">
              <small class="modal-label-premium text-white">Total R$</small>
              <h4 class="mb-0 font-weight-bold text-white">{{ formatarMoeda(valorTotal) }}</h4>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Modal de Abastecimento -->
    <abastecimento-modal 
      ref="modalAbastecimento" 
      :veiculo-id="veiculoId"
      @salvou="buscarAbastecimentos"
    ></abastecimento-modal>
  </div>
</template>

<script>
import moment from 'moment'
import AbastecimentoModal from './AbastecimentoModal.vue'
import Tools from '../../../../tools/Tools'

export default {
  name: 'AbastecimentosTab',
  components: {
    AbastecimentoModal
  },
  props: {
    veiculoId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      lista: [],
      fields: [
        { key: 'ID', label: 'ID', sortable: true, thStyle: 'width: 60px;' },
        { key: 'DATA', label: 'Data', sortable: true, thStyle: 'width: 110px;' },
        { key: 'NOTA_FISCAL', label: 'NF', sortable: true, thStyle: 'width: 100px;' },
        { key: 'LITROS', label: 'Litros', sortable: true, thStyle: 'width: 100px;', tdClass: 'text-right' },
        { key: 'PRECO', label: 'Preço/L', sortable: true, thStyle: 'width: 100px;', tdClass: 'text-right' },
        { key: 'TOTAL', label: 'Total', sortable: true, thStyle: 'width: 120px;', tdClass: 'text-right' },
        { key: 'ULTIMA_KM', label: 'KM', sortable: true, thStyle: 'width: 100px;', tdClass: 'text-right' },
        { key: 'OP_GERA_TITULO', label: 'Título', sortable: true, thStyle: 'width: 100px;', tdClass: 'text-center' },
        { key: 'actions', label: 'Ações', thStyle: 'width: 120px;', tdClass: 'text-center' }
      ]
    }
  },
  computed: {
    totalAbastecimentos () {
      return this.lista.length
    },
    abastecimentosComTitulo () {
      return this.lista.filter(a => a.OP_GERA_TITULO === '1').length
    },
    titulosEmAberto () {
      return this.lista.filter(a => a.OP_GERA_TITULO !== '1').length
    },
    valorTotal () {
      return this.lista.reduce((acc, item) => acc + (parseFloat(item.TOTAL) || 0), 0)
    }
  },
  mounted () {
    this.buscarAbastecimentos()
  },
  methods: {
    async buscarAbastecimentos () {
      this.$store.commit('controle/setAguardando', true)
      try {
        const response = await this.$http.get(`/api/protected/veiculo/abastecimento/listar/${this.veiculoId}`)
        this.lista = response.data || []
      } catch (error) {
        console.error('Erro ao buscar abastecimentos:', error)
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', 'Erro ao buscar abastecimentos')
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    novoAbastecimento () {
      this.$refs.modalAbastecimento.abrir()
    },
    editarAbastecimento (item) {
      this.$refs.modalAbastecimento.abrir(item.ID)
    },
    async gerarTitulo (item) {
      // Validações conforme código Delphi
      if (item.OP_GERA_TITULO === '1' && item.APAGAR_ID > 0) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', `Não é possível prosseguir! Essa movimentação já gerou um título no Contas a Pagar com número: ABT${item.ID}/1. Para continuar, será necessário excluir o título no Contas a Pagar!`)
        return
      }

      if (!item.FORNECEDOR_ID) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', 'Informe o fornecedor da movimentação!')
        return
      }

      if (!item.DATA_VENCIMENTO) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', 'Informe a data do vencimento da movimentação!')
        return
      }

      if (!(await this.$swalConfirm('Gerar Título Financeiro A Pagar?', 'question'))) return

      try {
        const response = await this.$http.post('/api/protected/veiculo/abastecimento/gerarTitulo', {
          id: item.ID,
          login: localStorage.getItem('login')
        })

        if (response.data.sucesso) {
          Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Título gerado com sucesso!')
          this.buscarAbastecimentos()
        } else {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', response.data.mensagem || 'Erro ao gerar título')
        }
      } catch (error) {
        console.error('Erro ao gerar título:', error)
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', error.response?.data?.mensagem || 'Erro ao gerar título')
      }
    },
    async excluir (item) {
      if (item.OP_GERA_TITULO === '1' && item.APAGAR_ID > 0) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Não é possível excluir! Este abastecimento gerou um título a pagar. Exclua o título primeiro.')
        return
      }

      if (!(await this.$swalConfirm(`Excluir o abastecimento #${item.ID}?`, 'warning'))) return

      try {
        await this.$http.post('/api/protected/veiculo/abastecimento/delete', {
          id: item.ID,
          login: localStorage.getItem('login')
        })

        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Abastecimento excluído com sucesso!')
        this.buscarAbastecimentos()
      } catch (error) {
        console.error('Erro ao excluir:', error)
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', error.response?.data || 'Erro ao excluir')
      }
    },
    formatarData (data) {
      return data ? moment(data).format('DD/MM/YYYY') : '-'
    },
    formatarMoeda (valor) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0)
    },
    formatarNumero (valor) {
      return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
    }
  }
}
</script>

<style scoped>
.modal-label-premium {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 0.4rem;
  display: block;
}

.opacity-50 {
  opacity: 0.5;
}
</style>



// WEBPACK FOOTER //
// src/views/cadastros/veiculo/components/AbastecimentosTab.vue